import {
  startTransition,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Database,
  Search,
  Stars,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Weight,
  X,
} from "lucide-react";
import { toast } from "sonner";
import MainLayout from "@/layouts/MainLayout";
import {
  LocalPokemonEntry,
  LANGUAGE_CODE_MAPPING,
  MoveDetail,
  Pokemon,
  PokemonMove,
  PokemonMoveVersionGroupDetail,
  fetchBatchSpeciesData,
  fetchBatchAbilityLocalizations,
  fetchBatchMoveDetails,
  fetchMoveDetails,
  fetchPokemon,
  fetchSpeciesLocalizedNamesByIds,
  getLocalPokemonDb,
  getLocalPokemonDbMeta,
  loadLocalPokemonDb,
  loadMoreLocalPokemonDb,
  prefetchRemainingLocalPokemonDb,
  getPokemonBasicInfoMapWithOptions,
} from "@/services/pokeApi";
import { useLanguage } from "@/services/languageService";
import { getLocalMoveEffectText } from "@/services/moveEffectTranslationDb";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import StatBar from "@/components/StatBar";
import PokemonTypeTag from "@/components/PokemonTypeTag";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 24;
const MOVE_SPOTLIGHT_LIMIT = 6;

const GENERATION_ORDER = [
  "generation-i",
  "generation-ii",
  "generation-iii",
  "generation-iv",
  "generation-v",
  "generation-vi",
  "generation-vii",
  "generation-viii",
  "generation-ix",
] as const;

const GENERATION_LABELS: Record<string, string> = {
  "generation-i": "Generation I",
  "generation-ii": "Generation II",
  "generation-iii": "Generation III",
  "generation-iv": "Generation IV",
  "generation-v": "Generation V",
  "generation-vi": "Generation VI",
  "generation-vii": "Generation VII",
  "generation-viii": "Generation VIII",
  "generation-ix": "Generation IX",
};

const TYPE_GRADIENTS: Record<string, string> = {
  grass: "from-emerald-200 via-lime-100 to-white",
  fire: "from-orange-200 via-amber-100 to-white",
  water: "from-sky-200 via-cyan-100 to-white",
  electric: "from-yellow-200 via-amber-100 to-white",
  psychic: "from-pink-200 via-rose-100 to-white",
  dragon: "from-indigo-200 via-violet-100 to-white",
  dark: "from-slate-300 via-zinc-100 to-white",
  fairy: "from-fuchsia-200 via-pink-100 to-white",
  steel: "from-slate-200 via-slate-100 to-white",
  ghost: "from-violet-200 via-purple-100 to-white",
  rock: "from-stone-300 via-stone-100 to-white",
  ground: "from-amber-300 via-yellow-100 to-white",
  bug: "from-lime-200 via-lime-100 to-white",
  fighting: "from-red-200 via-rose-100 to-white",
  poison: "from-purple-200 via-fuchsia-100 to-white",
  ice: "from-cyan-200 via-sky-100 to-white",
  flying: "from-indigo-100 via-sky-100 to-white",
  normal: "from-slate-200 via-zinc-100 to-white",
};

const VERSION_GROUP_PRIORITY = [
  "scarlet-violet",
  "legends-arceus",
  "brilliant-diamond-and-shining-pearl",
  "sword-shield",
  "lets-go-pikachu-lets-go-eevee",
  "ultra-sun-ultra-moon",
  "sun-moon",
  "omega-ruby-alpha-sapphire",
  "x-y",
  "black-2-white-2",
  "black-white",
  "heartgold-soulsilver",
  "platinum",
  "diamond-pearl",
  "firered-leafgreen",
  "emerald",
  "ruby-sapphire",
  "crystal",
  "gold-silver",
  "yellow",
  "red-blue",
] as const;

const VERSION_GROUP_LABELS: Record<string, string> = {
  "scarlet-violet": "pokedexVersionGroupScarletViolet",
  "legends-arceus": "Legends: Arceus",
  "brilliant-diamond-and-shining-pearl": "Brilliant Diamond / Shining Pearl",
  "sword-shield": "Sword / Shield",
  "lets-go-pikachu-lets-go-eevee": "Let's Go Pikachu / Eevee",
  "ultra-sun-ultra-moon": "Ultra Sun / Ultra Moon",
  "sun-moon": "Sun / Moon",
  "omega-ruby-alpha-sapphire": "Omega Ruby / Alpha Sapphire",
  "x-y": "X / Y",
  "black-2-white-2": "Black 2 / White 2",
  "black-white": "Black / White",
  "heartgold-soulsilver": "HeartGold / SoulSilver",
  "platinum": "Platinum",
  "diamond-pearl": "Diamond / Pearl",
  "firered-leafgreen": "FireRed / LeafGreen",
  "emerald": "Emerald",
  "ruby-sapphire": "Ruby / Sapphire",
  "crystal": "Crystal",
  "gold-silver": "Gold / Silver",
  "yellow": "Yellow",
  "red-blue": "Red / Blue",
};

const LANGUAGE_FALLBACKS: Record<string, string[]> = {
  "zh-CN": ["zh", "zh-TW", "en"],
  "zh-TW": ["zh", "zh-CN", "en"],
  ja: ["en"],
  ko: ["en"],
  fr: ["en"],
  de: ["en"],
  es: ["es-ES", "es-MX", "en"],
  it: ["en"],
  ru: ["en"],
  pt: ["pt-BR", "es", "en"],
  ar: ["en"],
  hi: ["en"],
  id: ["en"],
  tr: ["en"],
  vi: ["en"],
};

const DEFAULT_FALLBACKS = ["en", "ja", "fr", "es", "de"];
const MOVE_METHOD_PRIORITY = ["level-up", "machine", "egg", "tutor", "evolution", "form-change"] as const;

type SortOption = "number-asc" | "name-asc" | "rarity-desc" | "height-desc" | "weight-desc";
type MoveSpotlight = {
  name: string;
  versionGroup: string;
  primaryRoute: PokemonMoveVersionGroupDetail;
  routes: PokemonMoveVersionGroupDetail[];
};

const SORT_OPTION_KEYS: Record<SortOption, string> = {
  "number-asc": "pokedexSortNumber",
  "name-asc": "pokedexSortName",
  "rarity-desc": "pokedexSortRarity",
  "height-desc": "pokedexSortHeight",
  "weight-desc": "pokedexSortWeight",
};

const formatPokemonName = (name: string) =>
  name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const normalizeSearchText = (value: string) =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getLanguageCandidates = (language: string) => {
  const candidates: string[] = [];
  const pushUnique = (code: string) => {
    const normalized = code.toLowerCase();
    if (!candidates.includes(normalized)) {
      candidates.push(normalized);
    }
  };

  pushUnique(language);
  (LANGUAGE_CODE_MAPPING[language] || []).forEach(pushUnique);

  const fallbacks = LANGUAGE_FALLBACKS[language] || DEFAULT_FALLBACKS;
  fallbacks.forEach((fallback) => {
    pushUnique(fallback);
    (LANGUAGE_CODE_MAPPING[fallback] || []).forEach(pushUnique);
  });

  DEFAULT_FALLBACKS.forEach((fallback) => {
    pushUnique(fallback);
    (LANGUAGE_CODE_MAPPING[fallback] || []).forEach(pushUnique);
  });

  return candidates;
};

const formatSlugLabel = (value: string) =>
  value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatHeight = (height: number) => `${(height / 10).toFixed(1)} m`;

const formatWeight = (weight: number) => `${(weight / 10).toFixed(1)} kg`;

const getFallbackArtworkUrl = (pokemonId?: number | null) =>
  pokemonId
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemonId}.png`
    : "";

const IMAGE_RETRY_LIMIT_PER_SOURCE = 2;

const withCacheBust = (url: string, attempt: number) => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}retry=${attempt}-${Date.now()}`;
};

const getCardImageSources = (entry: LocalPokemonEntry) => [
  entry.spriteUrl,
  getFallbackArtworkUrl(entry.id),
  "/placeholder.svg",
].filter((value): value is string => Boolean(value));

const getPokemonArtworkUrl = (pokemon: Pokemon | null, entry: LocalPokemonEntry | null) =>
  pokemon?.sprites?.other?.["official-artwork"]?.front_default ||
  pokemon?.sprites?.other?.home?.front_default ||
  pokemon?.sprites?.front_default ||
  entry?.spriteUrl ||
  getFallbackArtworkUrl(entry?.id);

const getEvolutionStageLabelKey = (stage: LocalPokemonEntry["evolutionStage"]) => {
  if (stage === "fully-evolved") return "evolutionStageFullyEvolved";
  if (stage === "not-fully-evolved") return "evolutionStageNotFullyEvolved";
  return "evolutionStageAll";
};

// Helper to get localized name from map for Pokedex entries
const getLocalizedNameFromMap = (
  entryId: number,
  language: string,
  localizedNamesMap: Record<number, Record<string, string>>
) => {
  const names = localizedNamesMap[entryId];
  if (!names || Object.keys(names).length === 0) {
    return null;
  }

  if (names[language] || names[language.toLowerCase()]) {
    return names[language] || names[language.toLowerCase()];
  }

  const languageCandidates = getLanguageCandidates(language);
  for (const candidate of languageCandidates) {
    if (names[candidate]) {
      return names[candidate];
    }
  }

  return null;
};

const getLocalizedPokemonName = (pokemon: Pokemon | null, language: string) => {
  if (!pokemon?.localizedNames || Object.keys(pokemon.localizedNames).length === 0) {
    return pokemon ? formatPokemonName(pokemon.name) : "";
  }

  if (pokemon.localizedNames[language] || pokemon.localizedNames[language.toLowerCase()]) {
    return pokemon.localizedNames[language] || pokemon.localizedNames[language.toLowerCase()];
  }

  const languageCandidates = getLanguageCandidates(language);
  for (const candidate of languageCandidates) {
    if (pokemon.localizedNames[candidate]) {
      return pokemon.localizedNames[candidate];
    }
  }

  const anyLanguage = Object.keys(pokemon.localizedNames)[0];
  return anyLanguage ? pokemon.localizedNames[anyLanguage] : formatPokemonName(pokemon.name);
};

const getLocalizedAbilityName = (
  ability: Pokemon["abilities"][number],
  language: string
) => {
  const localizedNames = ability.ability.localizedNames;
  if (!localizedNames || Object.keys(localizedNames).length === 0) {
    return formatPokemonName(ability.ability.name);
  }

  if (localizedNames[language]) {
    return localizedNames[language];
  }

  const fallbacks = LANGUAGE_FALLBACKS[language] || DEFAULT_FALLBACKS;
  for (const fallbackLang of fallbacks) {
    if (localizedNames[fallbackLang]) {
      return localizedNames[fallbackLang];
    }
  }

  for (const fallbackLang of DEFAULT_FALLBACKS) {
    if (localizedNames[fallbackLang]) {
      return localizedNames[fallbackLang];
    }
  }

  const anyLanguage = Object.keys(localizedNames)[0];
  return anyLanguage ? localizedNames[anyLanguage] : formatPokemonName(ability.ability.name);
};

const getApiLanguageCandidates = (language: string) => {
  const candidates: string[] = [];
  const appendCandidates = (code: string) => {
    const mappedCodes = LANGUAGE_CODE_MAPPING[code] || [code];
    mappedCodes.forEach((mappedCode) => {
      const normalizedCode = mappedCode.toLowerCase();
      if (!candidates.includes(normalizedCode)) {
        candidates.push(normalizedCode);
      }
    });
  };

  appendCandidates(language);
  DEFAULT_FALLBACKS.forEach(appendCandidates);

  return candidates;
};

// For move effect text: do not fall back to other languages.
// If the API doesn't have the requested language, return null and let local DB override.
const getApiLanguageCandidatesStrict = (language: string) => {
  const candidates: string[] = [];
  const appendCandidates = (code: string) => {
    const mappedCodes = LANGUAGE_CODE_MAPPING[code] || [code];
    mappedCodes.forEach((mappedCode) => {
      const normalizedCode = mappedCode.toLowerCase();
      if (!candidates.includes(normalizedCode)) {
        candidates.push(normalizedCode);
      }
    });
  };

  appendCandidates(language);
  return candidates;
};

const getLocalizedMoveField = <T extends { language: { name: string } }>(
  entries: T[] | undefined,
  language: string,
  selectText: (entry: T) => string | undefined
) => {
  if (!entries || entries.length === 0) return null;

  const languageCandidates = getApiLanguageCandidates(language);
  for (const candidate of languageCandidates) {
    const match = entries.find((entry) => entry.language.name.toLowerCase() === candidate);
    const text = match ? selectText(match)?.trim() : "";
    if (text) {
      return text;
    }
  }

  for (const entry of entries) {
    const text = selectText(entry)?.trim();
    if (text) {
      return text;
    }
  }

  return null;
};

const getLocalizedMoveName = (move: MoveDetail | null, language: string, fallbackName: string) =>
  getLocalizedMoveField(move?.names, language, (entry) => entry.name) || formatPokemonName(fallbackName);

const getLocalizedMoveEffectStrict = (move: MoveDetail | null, language: string) => {
  if (!move?.effect_entries || move.effect_entries.length === 0) return null;

  const candidates = getApiLanguageCandidatesStrict(language);
  for (const candidate of candidates) {
    const match = move.effect_entries.find(
      (entry) => entry.language.name.toLowerCase() === candidate
    );
    const text = match ? (match.effect || match.short_effect)?.trim() : "";
    if (text) return text;
  }

  return null;
};

const getMoveEffectText = (move: MoveDetail | null, language: string) => {
  const rawText = getLocalizedMoveEffectStrict(move, language);
  if (!rawText) return null;

  return rawText
    .replace(/\$effect_chance/g, move?.effect_chance != null ? String(move.effect_chance) : "")
    .replace(/\f/g, " ")
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getMoveEffectTextWithLocalFallback = async (
  moveName: string,
  move: MoveDetail | null,
  language: string
): Promise<string | null> => {
  // Local DB should override API to ensure correct language output.
  const localText = await getLocalMoveEffectText(moveName, language as any);
  if (localText) return localText;

  return getMoveEffectText(move, language);
};

const getMoveMethodPriority = (method: string) => {
  const index = MOVE_METHOD_PRIORITY.indexOf(method as (typeof MOVE_METHOD_PRIORITY)[number]);
  return index === -1 ? MOVE_METHOD_PRIORITY.length : index;
};

const getMoveMethodLabelKey = (method: string) => {
  switch (method) {
    case "level-up":
      return "pokedexMoveMethodLevelUp";
    case "machine":
      return "pokedexMoveMethodMachine";
    case "egg":
      return "pokedexMoveMethodEgg";
    case "tutor":
      return "pokedexMoveMethodTutor";
    case "evolution":
      return "pokedexMoveMethodEvolution";
    case "form-change":
      return "pokedexMoveMethodFormChange";
    default:
      return "pokedexMoveMethodSpecial";
  }
};

const getDamageClassLabelKey = (damageClass: string) => {
  switch (damageClass) {
    case "physical":
      return "pokedexMoveDamagePhysical";
    case "special":
      return "pokedexMoveDamageSpecial";
    case "status":
    default:
      return "pokedexMoveDamageStatus";
  }
};

const formatVersionGroupLabel = (versionGroup: string, t: (key: string) => string) => {
  const labelOrKey = VERSION_GROUP_LABELS[versionGroup];
  if (!labelOrKey) return formatSlugLabel(versionGroup);
  // When the mapping stores an i18n key, translate it. Otherwise show the literal label.
  return labelOrKey.startsWith("pokedex") ? t(labelOrKey) : labelOrKey;
};

const compareMoveRoutes = (
  left: PokemonMoveVersionGroupDetail,
  right: PokemonMoveVersionGroupDetail
) => {
  const methodDifference =
    getMoveMethodPriority(left.move_learn_method.name) - getMoveMethodPriority(right.move_learn_method.name);

  if (methodDifference !== 0) {
    return methodDifference;
  }

  if (left.move_learn_method.name === "level-up" && right.move_learn_method.name === "level-up") {
    const levelDifference = right.level_learned_at - left.level_learned_at;
    if (levelDifference !== 0) {
      return levelDifference;
    }
  }

  const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER;
  const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER;
  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  return right.level_learned_at - left.level_learned_at;
};

const dedupeMoveRoutes = (routes: PokemonMoveVersionGroupDetail[]) => {
  const seen = new Set<string>();

  return routes.filter((route) => {
    const routeKey = `${route.move_learn_method.name}:${route.level_learned_at}`;
    if (seen.has(routeKey)) {
      return false;
    }

    seen.add(routeKey);
    return true;
  });
};

const getPreferredVersionGroup = (moves: PokemonMove[]) => {
  const availableGroups = new Set(
    moves.flatMap((move) => move.version_group_details.map((detail) => detail.version_group.name))
  );

  return (
    VERSION_GROUP_PRIORITY.find((versionGroup) => availableGroups.has(versionGroup)) ||
    Array.from(availableGroups).sort((left, right) => left.localeCompare(right)).at(-1) ||
    null
  );
};

const compareMoveSpotlights = (left: MoveSpotlight, right: MoveSpotlight) => {
  const methodDifference =
    getMoveMethodPriority(left.primaryRoute.move_learn_method.name) -
    getMoveMethodPriority(right.primaryRoute.move_learn_method.name);

  if (methodDifference !== 0) {
    return methodDifference;
  }

  if (
    left.primaryRoute.move_learn_method.name === "level-up" &&
    right.primaryRoute.move_learn_method.name === "level-up"
  ) {
    const levelDifference = right.primaryRoute.level_learned_at - left.primaryRoute.level_learned_at;
    if (levelDifference !== 0) {
      return levelDifference;
    }
  }

  return left.name.localeCompare(right.name);
};

const buildMoveSpotlights = (pokemon: Pokemon | null) => {
  if (!pokemon) {
    return {
      versionGroup: null,
      moves: [] as MoveSpotlight[],
    };
  }

  const versionGroup = getPreferredVersionGroup(pokemon.moves);
  if (!versionGroup) {
    return {
      versionGroup: null,
      moves: [] as MoveSpotlight[],
    };
  }

  const candidates = pokemon.moves
    .map((pokemonMove) => {
      const routes = dedupeMoveRoutes(
        pokemonMove.version_group_details
          .filter((detail) => detail.version_group.name === versionGroup)
          .slice()
          .sort(compareMoveRoutes)
      );

      if (routes.length === 0) {
        return null;
      }

      return {
        name: pokemonMove.move.name,
        versionGroup,
        primaryRoute: routes[0],
        routes,
      };
    })
    .filter((move): move is MoveSpotlight => Boolean(move));

  const selectedMoves: MoveSpotlight[] = [];
  const usedNames = new Set<string>();

  MOVE_METHOD_PRIORITY.forEach((method) => {
    const bestCandidate = candidates
      .filter((candidate) => !usedNames.has(candidate.name) && candidate.primaryRoute.move_learn_method.name === method)
      .sort(compareMoveSpotlights)[0];

    if (!bestCandidate) {
      return;
    }

    selectedMoves.push(bestCandidate);
    usedNames.add(bestCandidate.name);
  });

  if (selectedMoves.length < MOVE_SPOTLIGHT_LIMIT) {
    candidates
      .filter((candidate) => !usedNames.has(candidate.name))
      .sort(compareMoveSpotlights)
      .slice(0, MOVE_SPOTLIGHT_LIMIT - selectedMoves.length)
      .forEach((candidate) => {
        selectedMoves.push(candidate);
        usedNames.add(candidate.name);
      });
  }

  return {
    versionGroup,
    moves: selectedMoves.slice(0, MOVE_SPOTLIGHT_LIMIT),
  };
};

const formatMoveRouteLevel = (route: PokemonMoveVersionGroupDetail, t: (key: string) => string) =>
  route.move_learn_method.name === "level-up" ? `${t("pokedexMovesLevel")} ${route.level_learned_at}` : null;

const PokedexPage = () => {
  const { t, currentLanguage } = useLanguage();
  const [entries, setEntries] = useState<LocalPokemonEntry[]>([]);
  const [isDatabaseLoading, setIsDatabaseLoading] = useState(true);
  const [databaseFailed, setDatabaseFailed] = useState(false);
  const [dbMeta, setDbMeta] = useState(() => getLocalPokemonDbMeta());
  const [searchQuery, setSearchQuery] = useState("");
  const [localizedNamesMap, setLocalizedNamesMap] = useState<Record<number, Record<string, string>>>({});
  const [generationFilter, setGenerationFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState<SortOption>("number-asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEntry, setSelectedEntry] = useState<LocalPokemonEntry | null>(null);
  const [selectedPokemon, setSelectedPokemon] = useState<Pokemon | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [selectedMoveName, setSelectedMoveName] = useState<string | null>(null);
  const [moveDetailsByName, setMoveDetailsByName] = useState<Record<string, MoveDetail>>({});
  const [isMoveDetailsLoading, setIsMoveDetailsLoading] = useState(false);
  const [isMoveSpotlightOpen, setIsMoveSpotlightOpen] = useState(false);
  const moveSectionRef = useRef<HTMLElement | null>(null);
  const moveSpotlightBodyRef = useRef<HTMLDivElement | null>(null);
  const pokedexPageMountedRef = useRef(false);

  const deferredSearchQuery = useDeferredValue(searchQuery);
  const normalizedSearchQuery = useMemo(
    () => normalizeSearchText(deferredSearchQuery),
    [deferredSearchQuery]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    pokedexPageMountedRef.current = true;
    return () => {
      pokedexPageMountedRef.current = false;
    };
  }, []);

  const availableTypes = useMemo(
    () =>
      Array.from(new Set(entries.flatMap((entry) => entry.types))).sort((left, right) =>
        left.localeCompare(right)
      ),
    [entries]
  );

  const filteredEntries = useMemo(() => {
    const filtered = entries.filter((entry) => {
      const rawQuery = normalizedSearchQuery;
      const queryWithoutHash = rawQuery.startsWith("#") ? rawQuery.slice(1) : rawQuery;
      const localizedName = getLocalizedNameFromMap(entry.id, currentLanguage, localizedNamesMap);

      const searchPool = [
        entry.name,
        formatPokemonName(entry.name),
        localizedName || "",
        entry.id.toString(),
        `#${entry.id.toString()}`,
        entry.id.toString().padStart(3, "0"),
        `#${entry.id.toString().padStart(3, "0")}`,
      ].map(normalizeSearchText);

      const matchesSearch =
        rawQuery.length === 0 ||
        searchPool.some((candidate) => candidate.includes(rawQuery)) ||
        (queryWithoutHash.length > 0 &&
          searchPool.some((candidate) => candidate.includes(queryWithoutHash)));

      const matchesGeneration = generationFilter === "all" || entry.generation === generationFilter;
      const matchesType = typeFilter === "all" || entry.types.includes(typeFilter);

      return matchesSearch && matchesGeneration && matchesType;
    });

    filtered.sort((left, right) => {
      switch (sortBy) {
        case "name-asc":
          return (
            (getLocalizedNameFromMap(left.id, currentLanguage, localizedNamesMap) || formatPokemonName(left.name))
              .localeCompare(
                getLocalizedNameFromMap(right.id, currentLanguage, localizedNamesMap) ||
                  formatPokemonName(right.name)
              ) || left.id - right.id
          );
        case "rarity-desc":
          return right.rarityScore - left.rarityScore || left.id - right.id;
        case "height-desc":
          return right.height - left.height || left.id - right.id;
        case "weight-desc":
          return right.weight - left.weight || left.id - right.id;
        case "number-asc":
        default:
          return left.id - right.id;
      }
    });

    return filtered;
  }, [currentLanguage, entries, generationFilter, localizedNamesMap, normalizedSearchQuery, sortBy, typeFilter]);

  /** 未使用搜索/世代/属性筛选时，列表语义为「整本图鉴」，分页与匹配数应对齐索引里的总数，而不是当前已加载的分片条数。 */
  const isCatalogWideView = useMemo(
    () =>
      normalizedSearchQuery.length === 0 &&
      generationFilter === "all" &&
      typeFilter === "all",
    [normalizedSearchQuery, generationFilter, typeFilter]
  );

  const displayedMatchCount = isCatalogWideView ? dbMeta.total : filteredEntries.length;
  const listLengthForPaging = isCatalogWideView ? dbMeta.total : filteredEntries.length;

  const totalPages = Math.max(1, Math.ceil(listLengthForPaging / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedEntries = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * PAGE_SIZE;
    return filteredEntries.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredEntries, safeCurrentPage]);

  const activeFilterCount =
    (searchQuery.trim() ? 1 : 0) +
    (generationFilter !== "all" ? 1 : 0) +
    (typeFilter !== "all" ? 1 : 0);

  useEffect(() => {
    document.title = `${t("pokedexPageTitle")} | ${t("subFooterRandomPokemonGeneratorLink")}`;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("pokedexPageSubtitle"));
    }
  }, [currentLanguage, t]);

  useEffect(() => {
    let isActive = true;

    const initializeDatabase = async () => {
      setIsDatabaseLoading(true);
      setDatabaseFailed(false);

      const loaded = await loadLocalPokemonDb();
      if (!isActive) return;

      if (loaded) {
        setEntries(getLocalPokemonDb());
        setDbMeta(getLocalPokemonDbMeta());
      } else {
        setDatabaseFailed(true);
      }

      setIsDatabaseLoading(false);
    };

    initializeDatabase();

    return () => {
      isActive = false;
    };
  }, []);

  // Fetch localized names when language or entries change
  useEffect(() => {
    if (entries.length === 0) return;

    let isActive = true;

    const fetchTranslations = async () => {
      try {
        // Only read cached base info on page load to avoid heavy API build blocking UI.
        const basicInfoMap = await getPokemonBasicInfoMapWithOptions({ buildIfMissing: false });

        if (!isActive) return;

        const newLocalizedNamesMap: Record<number, Record<string, string>> = {};

        basicInfoMap.forEach((info, id) => {
          if (info.localizedNames && Object.keys(info.localizedNames).length > 0) {
            newLocalizedNamesMap[id] = info.localizedNames;
          }
        });

        const pageIds = paginatedEntries.map((e) => e.id);
        const needsSpeciesFetch = pageIds.filter((id) => {
          const names = newLocalizedNamesMap[id];
          if (!names || Object.keys(names).length === 0) return true;
          if (names[currentLanguage] || names[currentLanguage.toLowerCase()]) return false;
          return true;
        });

        if (needsSpeciesFetch.length > 0) {
          const fetched = await fetchSpeciesLocalizedNamesByIds(needsSpeciesFetch);
          if (!isActive) return;
          Object.assign(newLocalizedNamesMap, fetched);
        }

        if (isActive) {
          setLocalizedNamesMap((prev) => ({ ...prev, ...newLocalizedNamesMap }));
        }
      } catch (error) {
        console.error("Failed to fetch localized names:", error);
      }
    };

    fetchTranslations();

    return () => {
      isActive = false;
    };
  }, [currentLanguage, entries, paginatedEntries]);

  useEffect(() => {
    startTransition(() => {
      setCurrentPage(1);
    });
  }, [deferredSearchQuery, generationFilter, typeFilter, sortBy]);

  // Load more local DB chunks when user navigates deeper pages.
  useEffect(() => {
    if (isDatabaseLoading || databaseFailed) return;
    const meta = getLocalPokemonDbMeta();
    setDbMeta(meta);
    // If user is near/at end of loaded entries, fetch one more chunk.
    const needed = safeCurrentPage * PAGE_SIZE;
    if (meta.loaded < needed && meta.loaded < meta.total) {
      loadMoreLocalPokemonDb()
        .then(() => {
          setEntries(getLocalPokemonDb());
          setDbMeta(getLocalPokemonDbMeta());
        })
        .catch(() => {});
    }
  }, [safeCurrentPage, isDatabaseLoading, databaseFailed]);

  // When user searches or applies filters, prefetch remaining chunks in the background so
  // results can match the full dex (1025) without loading every part on initial page load.
  useEffect(() => {
    if (isDatabaseLoading || databaseFailed) return;

    const meta = getLocalPokemonDbMeta();
    if (meta.loaded >= meta.total) return;

    const userNarrowingCatalog =
      normalizedSearchQuery.length > 0 ||
      generationFilter !== "all" ||
      typeFilter !== "all";

    if (!userNarrowingCatalog) return;

    const flush = () => {
      if (!pokedexPageMountedRef.current) return;
      setEntries(getLocalPokemonDb());
      setDbMeta(getLocalPokemonDbMeta());
    };

    void prefetchRemainingLocalPokemonDb(flush).then(flush).catch(() => {});
  }, [
    normalizedSearchQuery,
    generationFilter,
    typeFilter,
    isDatabaseLoading,
    databaseFailed,
  ]);

  useEffect(() => {
    if (!isDetailOpen || !selectedEntry) return;

    let isActive = true;

    const loadPokemonDetails = async () => {
      setIsDetailLoading(true);
      setSelectedMoveName(null);
      setMoveDetailsByName({});
      setIsMoveDetailsLoading(false);
      setIsMoveSpotlightOpen(false);
      try {
        const detail = await fetchPokemon(selectedEntry.id);

        if (!isActive) return;
        // Show core detail ASAP for cold starts.
        setSelectedPokemon({ ...detail });
        setIsDetailLoading(false);

        // Enrich localized names in the background (non-blocking).
        Promise.all([
          fetchBatchSpeciesData([detail]),
          fetchBatchAbilityLocalizations([detail], currentLanguage),
        ])
          .then(() => {
            if (!isActive) return;
            // Trigger a re-render so newly attached localized fields are reflected.
            setSelectedPokemon({ ...detail });
          })
          .catch((error) => {
            if (!isActive) return;
            console.error("Failed to enrich Pokedex detail:", error);
          });
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to load Pokedex detail:", error);
        toast.error(t("pokedexDetailError"));
      } finally {
        if (isActive) {
          // If fetchPokemon failed, we still need to clear loading.
          setIsDetailLoading(false);
        }
      }
    };

    loadPokemonDetails();

    return () => {
      isActive = false;
    };
  }, [isDetailOpen, selectedEntry, currentLanguage, t]);

  const moveSpotlights = useMemo(() => buildMoveSpotlights(selectedPokemon), [selectedPokemon]);

  useEffect(() => {
    if (moveSpotlights.moves.length === 0) {
      setSelectedMoveName(null);
      setIsMoveSpotlightOpen(false);
      return;
    }

    setSelectedMoveName((currentMoveName) =>
      currentMoveName && moveSpotlights.moves.some((move) => move.name === currentMoveName)
        ? currentMoveName
        : null
    );
  }, [moveSpotlights]);

  useEffect(() => {
    if (moveSpotlights.moves.length === 0) {
      setMoveDetailsByName({});
      setIsMoveDetailsLoading(false);
      return;
    }

    let isActive = true;

    // Prefetch all featured moves so grid labels can use API `names` (localized) without requiring a click.
    // Per-move fetch on spotlight open still uses cache via fetchMoveDetails.
    fetchBatchMoveDetails(moveSpotlights.moves.map((m) => m.name))
      .then((details) => {
        if (!isActive) return;
        setMoveDetailsByName((prev) => {
          const next = { ...prev };
          for (const d of details) {
            next[d.name] = d;
          }
          return next;
        });
      })
      .catch((error) => {
        if (!isActive) return;
        console.error("Failed to prefetch move details:", error);
      });

    return () => {
      isActive = false;
    };
  }, [moveSpotlights]);

  const selectedLocalizedName = getLocalizedPokemonName(selectedPokemon, currentLanguage);
  const selectedCardGradient = TYPE_GRADIENTS[selectedEntry?.types[0] ?? "normal"] ?? TYPE_GRADIENTS.normal;
  const selectedArtworkUrl = getPokemonArtworkUrl(selectedPokemon, selectedEntry);
  const selectedMoveVersionLabel = moveSpotlights.versionGroup
    ? formatVersionGroupLabel(moveSpotlights.versionGroup, t)
    : null;
  const selectedMoveSpotlight =
    selectedMoveName && isMoveSpotlightOpen
      ? moveSpotlights.moves.find((move) => move.name === selectedMoveName) || null
      : null;

  const selectedMoveDetail = useMemo(
    () => (selectedMoveSpotlight ? moveDetailsByName[selectedMoveSpotlight.name] || null : null),
    [moveDetailsByName, selectedMoveSpotlight]
  );

  const [selectedMoveIntro, setSelectedMoveIntro] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedMoveSpotlight) {
      setSelectedMoveIntro(null);
      return;
    }

    let isActive = true;
    // Clear previous move text immediately to avoid showing stale text
    setSelectedMoveIntro(null);
    getMoveEffectTextWithLocalFallback(
      selectedMoveSpotlight.name,
      selectedMoveDetail,
      currentLanguage
    ).then((text) => {
      if (isActive) setSelectedMoveIntro(text);
    });

    return () => {
      isActive = false;
    };
  }, [currentLanguage, selectedMoveDetail, selectedMoveSpotlight?.name]);

  useEffect(() => {
    if (!isMoveSpotlightOpen || !selectedMoveName) return;
    if (moveDetailsByName[selectedMoveName]) return;

    let isActive = true;
    setIsMoveDetailsLoading(true);
    fetchMoveDetails(selectedMoveName)
      .then((detail) => {
        if (!isActive) return;
        setMoveDetailsByName((prev) => ({ ...prev, [detail.name]: detail }));
      })
      .catch((error) => {
        if (!isActive) return;
        console.error("Failed to load move detail:", error);
      })
      .finally(() => {
        if (isActive) setIsMoveDetailsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isMoveSpotlightOpen, selectedMoveName, moveDetailsByName]);

  const handleOpenDetail = (entry: LocalPokemonEntry) => {
    setSelectedEntry(entry);
    setSelectedPokemon(null);
    setSelectedMoveName(null);
    setMoveDetailsByName({});
    setIsMoveSpotlightOpen(false);
    setIsDetailOpen(true);
  };

  const closeDetail = () => {
    setIsDetailOpen(false);
    setSelectedEntry(null);
    setSelectedPokemon(null);
    setSelectedMoveName(null);
    setMoveDetailsByName({});
    setIsMoveDetailsLoading(false);
    setIsMoveSpotlightOpen(false);
  };

  useEffect(() => {
    if (!isDetailOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      if (isMoveSpotlightOpen) {
        event.preventDefault();
        setSelectedMoveName(null);
        setIsMoveSpotlightOpen(false);
        return;
      }

      closeDetail();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDetailOpen, isMoveSpotlightOpen]);

  useEffect(() => {
    if (!selectedMoveSpotlight) return;

    const frameId = window.requestAnimationFrame(() => {
      moveSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      moveSpotlightBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [selectedMoveSpotlight]);

  const clearFilters = () => {
    startTransition(() => {
      setSearchQuery("");
      setGenerationFilter("all");
      setTypeFilter("all");
      setSortBy("number-asc");
    });
  };

  const handleCardImageError = (
    event: React.SyntheticEvent<HTMLImageElement, Event>,
    entry: LocalPokemonEntry
  ) => {
    const target = event.currentTarget;
    const sources = getCardImageSources(entry);
    const sourceIndex = Number.parseInt(target.dataset.sourceIndex || "0", 10);
    const retryCount = Number.parseInt(target.dataset.retryCount || "0", 10);

    // Retry the current source a couple of times (with cache-busting)
    if (sourceIndex < sources.length - 1 && retryCount < IMAGE_RETRY_LIMIT_PER_SOURCE) {
      const nextRetry = retryCount + 1;
      target.dataset.retryCount = String(nextRetry);
      target.src = withCacheBust(sources[sourceIndex], nextRetry);
      return;
    }

    // Move to next source and reset retry counter
    const nextSourceIndex = Math.min(sourceIndex + 1, sources.length - 1);
    target.dataset.sourceIndex = String(nextSourceIndex);
    target.dataset.retryCount = "0";
    target.src = sources[nextSourceIndex];
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-pokemon-red/5 px-4 py-6 sm:px-6 relative overflow-hidden">
        {/* 装饰性光晕 - 宝可梦风格 */}
        <div className="pointer-events-none absolute -left-20 top-20 h-64 w-64 rounded-full bg-pokemon-red/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-20 h-48 w-48 rounded-full bg-pokemon-yellow/10 blur-3xl" />
        <div className="pointer-events-none absolute left-1/3 top-1/2 h-32 w-32 rounded-full bg-pokemon-blue/10 blur-2xl" />
        
        <div className="mx-auto max-w-7xl space-y-5 relative z-10">
          <section className="rounded-[1.75rem] border border-slate-200/70 bg-white/88 px-6 py-6 shadow-[0_18px_45px_-38px_rgba(15,23,42,0.28)] backdrop-blur-sm sm:px-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-pokemon-red/30 bg-gradient-to-r from-pokemon-red/10 to-pokemon-yellow/10 px-3 py-1 text-xs font-semibold text-pokemon-red">
                  <Database className="h-4 w-4" />
                  {t("pokedexEyebrow")}
                </div>
                <div className="space-y-2">
                  <h1 className="text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
                    {t("pokedexPageTitle")}
                  </h1>
                  <p className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-base">
                    {t("pokedexPageSubtitle")}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5">
                  <span className="text-slate-400">{t("pokedexTotalEntries")}</span>
                  <span className="ml-2 font-semibold text-slate-900">{dbMeta.total}</span>
                </div>
                <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5">
                  <span className="text-slate-400">{t("pokedexFilteredEntries")}</span>
                  <span className="ml-2 font-semibold text-slate-900">{displayedMatchCount}</span>
                </div>
                <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5">
                  <span className="text-slate-400">{t("pokedexPageIndicator")}</span>
                  <span className="ml-2 font-semibold text-slate-900">{safeCurrentPage} / {totalPages}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-[1.5rem] border border-slate-200/80 bg-white/90 p-4 shadow-[0_16px_35px_-32px_rgba(15,23,42,0.24)] backdrop-blur-sm">
            <div className="grid gap-3 lg:grid-cols-[1.7fr_0.9fr_0.9fr_0.9fr_auto] lg:items-end">
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500">
                  {t("pokedexSearchLabel")}
                </p>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(event) => {
                      setSearchQuery(event.target.value);
                    }}
                    className="h-10 rounded-xl border-slate-200 pl-10 shadow-sm"
                    placeholder={t("pokedexSearchPlaceholder")}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500">
                  {t("generation")}
                </p>
                <Select value={generationFilter} onValueChange={setGenerationFilter}>
                  <SelectTrigger className="h-10 rounded-xl border-slate-200 shadow-sm">
                    <SelectValue placeholder={t("pokedexAllGenerations")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pokedexAllGenerations")}</SelectItem>
                    {GENERATION_ORDER.map((generation) => (
                      <SelectItem key={generation} value={generation}>
                        {t(GENERATION_LABELS[generation])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500">
                  {t("type")}
                </p>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-10 rounded-xl border-slate-200 shadow-sm">
                    <SelectValue placeholder={t("pokedexAllTypes")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pokedexAllTypes")}</SelectItem>
                    {availableTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500">
                  {t("pokedexSortLabel")}
                </p>
                <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
                  <SelectTrigger className="h-10 rounded-xl border-slate-200 shadow-sm">
                    <SelectValue placeholder={t("pokedexSortLabel")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(SORT_OPTION_KEYS) as SortOption[]).map((option) => (
                      <SelectItem key={option} value={option}>
                        {t(SORT_OPTION_KEYS[option])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 w-full rounded-xl border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 lg:w-auto"
                  onClick={clearFilters}
                  disabled={activeFilterCount === 0 && sortBy === "number-asc"}
                >
                  {t("pokedexClearFilters")}
                </Button>
              </div>
            </div>
          </section>

          {databaseFailed ? (
            <Card className="rounded-[1.75rem] border-red-100 bg-white/90">
              <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
                <Database className="h-10 w-10 text-pokemon-red" />
                <h2 className="text-2xl font-bold text-slate-900">{t("pokedexLoadErrorTitle")}</h2>
                <p className="max-w-xl text-sm leading-7 text-slate-600">{t("pokedexLoadErrorBody")}</p>
              </CardContent>
            </Card>
          ) : isDatabaseLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <Card key={index} className="overflow-hidden rounded-[1.5rem] border-white/70 bg-white/80">
                  <Skeleton className="h-48 w-full rounded-none" />
                  <CardContent className="space-y-4 p-4">
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-7 w-40" />
                    <div className="flex gap-2">
                      <Skeleton className="h-6 w-16 rounded-full" />
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredEntries.length === 0 ? (
            <Card className="rounded-[1.75rem] border-dashed border-slate-200 bg-white/90">
              <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
                <Search className="h-10 w-10 text-slate-400" />
                <h2 className="text-2xl font-bold text-slate-900">{t("pokedexEmptyTitle")}</h2>
                <p className="max-w-xl text-sm leading-7 text-slate-600">{t("pokedexEmptyBody")}</p>
                <Button
                  type="button"
                  className="rounded-full bg-pokemon-blue hover:bg-pokemon-blue/90"
                  onClick={clearFilters}
                >
                  {t("pokedexClearFilters")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {paginatedEntries.map((entry) => {
                  const gradient = TYPE_GRADIENTS[entry.types[0]] ?? TYPE_GRADIENTS.normal;

                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => handleOpenDetail(entry)}
                      className="group text-left"
                    >
                      <Card className="h-full overflow-hidden rounded-[1.5rem] border-white/70 bg-white/85 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_24px_55px_-35px_rgba(12,96,179,0.45)]">
                        <div className={cn("relative flex justify-center overflow-hidden bg-gradient-to-br p-5", gradient)}>
                          <div className="absolute left-4 top-4 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                            #{entry.id.toString().padStart(3, "0")}
                          </div>
                          <div className="absolute right-4 top-4 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                            {entry.rarityScore}/10
                          </div>
                          <img
                            src={entry.spriteUrl}
                            alt={getLocalizedNameFromMap(entry.id, currentLanguage, localizedNamesMap) || formatPokemonName(entry.name)}
                            className="h-36 w-36 object-contain transition-transform duration-500 group-hover:scale-105 animate-float"
                            style={{ animationDuration: `${4 + (entry.id % 3)}s` }}
                            loading="lazy"
                            decoding="async"
                            data-source-index="0"
                            data-retry-count="0"
                            onError={(event) => handleCardImageError(event, entry)}
                          />
                        </div>

                        <CardContent className="space-y-4 p-5">
                          <div className="space-y-1">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                              {t(GENERATION_LABELS[entry.generation] || "generation")}
                            </p>
                            <h2 className="text-xl font-bold text-slate-900">
                              {getLocalizedNameFromMap(entry.id, currentLanguage, localizedNamesMap) || formatPokemonName(entry.name)}
                            </h2>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {entry.types.map((type) => (
                              <PokemonTypeTag key={`${entry.id}-${type}`} type={type} />
                            ))}
                          </div>

                          <div className="grid grid-cols-2 gap-3 rounded-2xl bg-slate-50 p-3 text-sm text-slate-600">
                            <div>
                              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("heightLabel")}</p>
                              <p className="mt-1 font-semibold text-slate-800">{formatHeight(entry.height)}</p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("weightLabel")}</p>
                              <p className="mt-1 font-semibold text-slate-800">{formatWeight(entry.weight)}</p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            <span>{t(getEvolutionStageLabelKey(entry.evolutionStage))}</span>
                            <span className="text-pokemon-blue transition-transform duration-300 group-hover:translate-x-1">
                              {t("pokedexInspect")}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </button>
                  );
                })}
              </section>

              <div className="flex flex-col items-center justify-between gap-4 rounded-[1.5rem] border border-white/70 bg-white/80 px-4 py-4 shadow-sm sm:flex-row">
                <p className="text-sm text-slate-600">
                  {t("pokedexShowingResults", {
                    start: String((safeCurrentPage - 1) * PAGE_SIZE + 1),
                    end: String(Math.min(safeCurrentPage * PAGE_SIZE, displayedMatchCount)),
                    total: String(displayedMatchCount),
                  })}
                </p>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={safeCurrentPage === 1}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t("pokedexPrevious")}
                  </Button>
                  <div className="min-w-24 text-center text-sm font-semibold text-slate-600">
                    {t("pokedexPageStatus", {
                      page: String(safeCurrentPage),
                      total: String(totalPages),
                    })}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={safeCurrentPage >= totalPages}
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  >
                    {t("pokedexNext")}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {typeof document !== "undefined" &&
        isDetailOpen &&
        selectedEntry &&
        createPortal(
          <div className="fixed inset-0 z-[80] flex items-center justify-center px-3 py-4 sm:px-6 sm:py-8">
            <button
              type="button"
              aria-label="Close"
              className="absolute inset-0 bg-slate-950/55 backdrop-blur-[3px]"
              onClick={closeDetail}
            />

            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="pokedex-detail-title"
              aria-describedby="pokedex-detail-description"
              className="pokedex-detail-shell relative z-[81] w-full max-w-4xl overflow-hidden rounded-[2rem] border border-white/70 bg-[#f8fbff] shadow-[0_40px_120px_-52px_rgba(15,23,42,0.58)]"
            >
              <button
                type="button"
                onClick={closeDetail}
                className="absolute right-4 top-4 z-20 rounded-full border border-white/80 bg-white/90 p-2 text-slate-500 shadow-sm transition hover:text-slate-700"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>

              <div className="sr-only">
                <h2 id="pokedex-detail-title">
                  {selectedEntry ? formatPokemonName(selectedEntry.name) : t("pokedexPageTitle")}
                </h2>
                <p id="pokedex-detail-description">{t("pokedexDetailDescription")}</p>
              </div>

              <div className="max-h-[92vh] overflow-y-auto lg:overflow-hidden">
                <div className="grid gap-0 lg:grid-cols-[0.95fr_1.05fr]">
              <div className={cn("relative overflow-hidden bg-gradient-to-br px-6 py-8", selectedCardGradient)}>
                <div className="absolute -right-10 top-10 h-32 w-32 rounded-full bg-white/25 blur-2xl" />
                <div className="absolute -left-8 bottom-10 h-24 w-24 rounded-full bg-white/25 blur-2xl" />

                <div className="relative space-y-6">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="rounded-full border-white/80 bg-white/70 text-slate-700">
                      #{selectedEntry.id.toString().padStart(3, "0")}
                    </Badge>
                    <Badge className="rounded-full bg-slate-900/80 text-white hover:bg-slate-900/80">
                      {t(GENERATION_LABELS[selectedEntry.generation] || "generation")}
                    </Badge>
                  </div>

                  <div className="flex justify-center">
                    {isDetailLoading ? (
                      <Skeleton className="h-56 w-56 rounded-full bg-white/50" />
                    ) : (
                      <div className="relative flex h-60 w-60 items-center justify-center rounded-[2rem] bg-white/28 shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]">
                        <div className="absolute inset-4 rounded-[1.7rem] border border-white/30 bg-white/18" />
                        <img
                          src={selectedArtworkUrl}
                          alt={selectedLocalizedName || formatPokemonName(selectedEntry.name)}
                          className="relative h-56 w-56 object-contain drop-shadow-[0_24px_36px_rgba(15,23,42,0.16)] animate-float"
                          onError={(event) => {
                            const fallbackUrl = getFallbackArtworkUrl(selectedEntry.id);
                            if (fallbackUrl && event.currentTarget.src !== fallbackUrl) {
                              event.currentTarget.src = fallbackUrl;
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                        {t("pokedexInspect")}
                      </p>
                      <h2 className="mt-2 text-3xl font-black text-slate-900">
                        {isDetailLoading || !selectedPokemon
                          ? formatPokemonName(selectedEntry.name)
                          : selectedLocalizedName}
                      </h2>
                      <p className="mt-2 text-sm text-slate-600">
                        {selectedPokemon ? formatPokemonName(selectedPokemon.name) : formatPokemonName(selectedEntry.name)}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {selectedEntry.types.map((type) => (
                        <PokemonTypeTag key={`detail-${selectedEntry.id}-${type}`} type={type} className="shadow-sm" />
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-white/70 p-4 shadow-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("heightLabel")}</p>
                      <p className="mt-2 text-lg font-bold text-slate-900">
                        {formatHeight(selectedPokemon?.height ?? selectedEntry.height)}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-white/70 p-4 shadow-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("weightLabel")}</p>
                      <p className="mt-2 text-lg font-bold text-slate-900">
                        {formatWeight(selectedPokemon?.weight ?? selectedEntry.weight)}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-white/70 p-4 shadow-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("minimumRarity")}</p>
                      <p className="mt-2 text-lg font-bold text-slate-900">{selectedEntry.rarityScore}/10</p>
                    </div>
                    <div className="rounded-2xl bg-white/70 p-4 shadow-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("evolutionStage")}</p>
                      <p className="mt-2 text-lg font-bold text-slate-900">
                        {t(getEvolutionStageLabelKey(selectedEntry.evolutionStage))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid content-start gap-3 px-5 py-6">
                {isDetailLoading || !selectedPokemon ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm font-medium">{t("pokedexDetailLoading")}</span>
                    </div>
                    <Skeleton className="h-32 w-full rounded-[1.35rem]" />
                    <Skeleton className="h-32 w-full rounded-[1.35rem]" />
                    <Skeleton className="h-48 w-full rounded-[1.35rem]" />
                  </div>
                ) : (
                  <>
                    <section className="rounded-[1.35rem] border border-slate-200/80 bg-white p-4 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Stars className="h-3.5 w-3.5 text-pokemon-yellow" />
                        <h3 className="text-base font-bold text-slate-900">{t("pokedexOverviewTitle")}</h3>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,0.38fr)_minmax(0,0.62fr)]">
                        <div className="rounded-[1rem] bg-slate-50 p-3">
                          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("pokedexBaseExperience")}</p>
                          <p className="mt-1.5 text-base font-bold text-slate-900">{selectedPokemon.base_experience}</p>
                        </div>
                        <div className="rounded-[1rem] bg-slate-50 p-3">
                          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("pokedexAbilitiesTitle")}</p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {selectedPokemon.abilities.map((ability) => (
                              <Badge
                                key={ability.ability.name}
                                variant={ability.is_hidden ? "secondary" : "outline"}
                                className="rounded-full px-2 py-0.5 text-[10px]"
                              >
                                {getLocalizedAbilityName(ability, currentLanguage)}
                                {ability.is_hidden && " (Hidden)"}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="rounded-[1.35rem] border border-slate-200/80 bg-white p-4 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Database className="h-3.5 w-3.5 text-pokemon-blue" />
                        <h3 className="text-base font-bold text-slate-900">{t("pokedexStatsTitle")}</h3>
                      </div>
                      <div className="mt-3">
                        {selectedPokemon.stats.map((stat) => (
                          <StatBar key={stat.stat.name} name={stat.stat.name} value={stat.base_stat} compact />
                        ))}
                      </div>
                    </section>

                    <section
                      ref={moveSectionRef}
                      className={cn(
                        "relative overflow-hidden rounded-[1.35rem] border border-slate-200/80 bg-white p-3.5 shadow-sm transition-all duration-300",
                        isMoveSpotlightOpen && "z-10 shadow-[0_26px_60px_-38px_rgba(15,23,42,0.32)]"
                      )}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Weight className="h-3.5 w-3.5 text-pokemon-red" />
                          <h3 className="text-base font-bold text-slate-900">{t("pokedexMovesTitle")}</h3>
                        </div>
                        {selectedMoveVersionLabel && (
                          <div className="inline-flex items-center gap-1.5 rounded-full border border-pokemon-blue/15 bg-pokemon-blue/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-pokemon-blue">
                            <span>{t("pokedexMovesLearnset")}</span>
                            <span className="normal-case tracking-normal text-slate-600">{selectedMoveVersionLabel}</span>
                          </div>
                        )}
                      </div>

                      {moveSpotlights.moves.length === 0 ? (
                        <p className="mt-4 text-sm leading-7 text-slate-600">{t("pokedexMovesUnavailable")}</p>
                      ) : (
                        <div
                          className={cn(
                            "mt-3 grid auto-rows-fr grid-cols-2 gap-1.5 sm:gap-2 lg:grid-cols-3",
                            selectedMoveSpotlight && "relative min-h-[13rem] content-start sm:min-h-[14rem] lg:min-h-[13.5rem]"
                          )}
                        >
                          {moveSpotlights.moves.map((move) => {
                            const moveDetail = moveDetailsByName[move.name] || null;
                            const moveName = getLocalizedMoveName(moveDetail, currentLanguage, move.name);
                            const moveEnglishName = getLocalizedMoveName(moveDetail, "en", move.name);
                            const isSelected = selectedMoveName === move.name && isMoveSpotlightOpen;
                            const moveLevelLabel = formatMoveRouteLevel(move.primaryRoute, t);

                            if (isSelected) {
                              const moveIntro = selectedMoveIntro;

                              return (
                                <div
                                  key={move.name}
                                  ref={moveSpotlightBodyRef}
                                  className="move-spotlight-inline-card absolute inset-0 z-20 origin-top overflow-hidden rounded-[1rem] border border-pokemon-blue/15 bg-[linear-gradient(145deg,_rgba(255,255,255,0.99),_rgba(239,246,255,0.97))] p-2.5 shadow-[0_16px_34px_-34px_rgba(15,23,42,0.2)]"
                                >
                                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-pokemon-red/0 via-pokemon-blue/55 to-pokemon-yellow/0" />
                                  <div className="absolute -right-6 top-0 h-12 w-12 rounded-full bg-pokemon-blue/10 blur-3xl" />
                                  <div className="absolute -left-6 bottom-0 h-10 w-10 rounded-full bg-pokemon-yellow/10 blur-3xl" />

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedMoveName(null);
                                      setIsMoveSpotlightOpen(false);
                                    }}
                                    className="absolute right-2 top-2 z-10 rounded-full border border-white/80 bg-white/85 p-0.5 text-slate-500 shadow-sm transition hover:text-slate-700"
                                  >
                                    <X className="h-2.5 w-2.5" />
                                    <span className="sr-only">Close</span>
                                  </button>

                                  <div className="relative grid h-full gap-2 pr-5 sm:grid-cols-2 sm:pr-6">
                                    <div className="flex h-full min-w-0 flex-col gap-2">
                                      <div className="flex flex-wrap gap-1.5">
                                        {moveDetail && (
                                          <>
                                            <PokemonTypeTag
                                              type={moveDetail.type.name}
                                              className="rounded-full px-2 py-0.5 text-[10px] shadow-[0_8px_18px_-14px_rgba(15,23,42,0.24)]"
                                            />
                                            <Badge
                                              variant="outline"
                                              className="rounded-full border-white/80 bg-white/80 px-2 py-0.5 text-[10px] text-slate-700"
                                            >
                                              {t(getDamageClassLabelKey(moveDetail.damage_class.name))}
                                            </Badge>
                                          </>
                                        )}
                                        <Badge
                                          variant="outline"
                                          className="rounded-full border-pokemon-blue/15 bg-pokemon-blue/5 px-2 py-0.5 text-[10px] text-pokemon-blue"
                                        >
                                          {formatVersionGroupLabel(move.versionGroup, t)}
                                        </Badge>
                                      </div>

                                      <div>
                                        <h4 className="text-[15px] font-black leading-5 tracking-tight text-slate-900 sm:text-base">
                                          {moveName}
                                        </h4>
                                        {moveEnglishName !== moveName && (
                                          <p className="mt-0.5 text-[11px] leading-4 text-slate-500">{moveEnglishName}</p>
                                        )}
                                      </div>

                                      <div className="flex flex-wrap gap-1.5">
                                        <div className="rounded-full border border-white/80 bg-white/88 px-1.5 py-0.5 shadow-sm">
                                          <span className="text-[9px] uppercase tracking-[0.16em] text-slate-400">
                                            {t("pokedexMovesPower")}
                                          </span>
                                          <span className="ml-1 text-[11px] font-bold text-slate-900">
                                            {moveDetail?.power ?? "\u2014"}
                                          </span>
                                        </div>
                                        <div className="rounded-full border border-white/80 bg-white/88 px-1.5 py-0.5 shadow-sm">
                                          <span className="text-[9px] uppercase tracking-[0.16em] text-slate-400">
                                            {t("pokedexMovesAccuracy")}
                                          </span>
                                          <span className="ml-1 text-[11px] font-bold text-slate-900">
                                            {moveDetail?.accuracy ?? "\u2014"}
                                          </span>
                                        </div>
                                        <div className="rounded-full border border-white/80 bg-white/88 px-1.5 py-0.5 shadow-sm">
                                          <span className="text-[9px] uppercase tracking-[0.16em] text-slate-400">
                                            {t("pokedexMovesPP")}
                                          </span>
                                          <span className="ml-1 text-[11px] font-bold text-slate-900">
                                            {moveDetail?.pp ?? "\u2014"}
                                          </span>
                                        </div>
                                      </div>

                                      {isMoveDetailsLoading && !moveDetail ? (
                                        <Skeleton className="h-9 rounded-[0.8rem]" />
                                      ) : (
                                        <div className="flex flex-wrap gap-1.5">
                                          <Badge
                                            variant="outline"
                                            className="rounded-full border-white/80 bg-white/90 px-2 py-0.5 text-[10px] text-slate-700"
                                          >
                                            {t(getMoveMethodLabelKey(move.primaryRoute.move_learn_method.name))}
                                          </Badge>
                                          {moveLevelLabel && (
                                            <Badge
                                              variant="outline"
                                              className="rounded-full border-white/80 bg-white/90 px-2 py-0.5 text-[10px] text-slate-600"
                                            >
                                              {moveLevelLabel}
                                            </Badge>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    <div className="hidden h-full min-h-0 sm:block">
                                      {isMoveDetailsLoading && !moveDetail ? (
                                        <Skeleton className="h-full min-h-[6.25rem] rounded-[0.8rem]" />
                                      ) : moveIntro ? (
                                        <div className="scrollbar-hidden h-full overflow-y-auto rounded-[0.85rem] border border-white/80 bg-white/84 px-2.5 py-2 text-[11px] leading-4 text-slate-600 shadow-sm">
                                          <p className="whitespace-pre-line">
                                            {moveIntro}
                                          </p>
                                        </div>
                                      ) : (
                                        <div className="scrollbar-hidden flex h-full min-h-[6.25rem] items-center justify-center rounded-[0.85rem] border border-white/80 bg-white/84 px-2.5 py-2 text-[11px] leading-4 text-slate-500 shadow-sm">
                                          <p className="whitespace-pre-line text-center">
                                            {t("pokedexMovesEffectFallback")}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <button
                                key={move.name}
                                type="button"
                                onClick={() => {
                                  setSelectedMoveName(move.name);
                                  setIsMoveSpotlightOpen(true);
                                }}
                                className={cn(
                                  "group relative overflow-hidden rounded-[0.8rem] border border-slate-200/80 bg-[linear-gradient(180deg,_rgba(248,250,252,0.92),_rgba(255,255,255,0.98))] px-2 py-1.5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-pokemon-blue/15 hover:bg-white",
                                  isMoveSpotlightOpen &&
                                    "pointer-events-none scale-[0.99] opacity-[0.2]"
                                )}
                              >
                                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-pokemon-red/0 via-pokemon-blue/35 to-pokemon-yellow/0" />
                                <div className="absolute -right-4 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full bg-pokemon-blue/5 blur-2xl transition-all duration-300 group-hover:bg-pokemon-blue/10" />
                                <div className="relative flex min-h-[2.45rem] items-center">
                                  <h4
                                    className="text-[13px] font-bold leading-[1.05rem] tracking-tight text-slate-900 transition-colors duration-300 group-hover:text-slate-950"
                                    style={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {moveName}
                                  </h4>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  </>
                )}
              </div>
            </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </MainLayout>
  );
};

export default PokedexPage;